/* Shared API reference content — used on the public /api-reference page
   and inside the dashboard at /dashboard/api-docs. */

function CodeBlock({ children }: { children: string }) {
  return (
    <pre
      style={{
        background: '#0b1120',
        color: '#d1fae5',
        borderRadius: 10,
        padding: '14px 16px',
        fontSize: 12,
        lineHeight: 1.6,
        overflowX: 'auto',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}
    >
      {children}
    </pre>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    GET: { bg: '#eff6ff', text: '#1d4ed8' },
    POST: { bg: '#f0fdf4', text: '#15803d' },
  };
  const c = colors[method] || { bg: '#f3f4f6', text: '#374151' };
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        fontSize: 10,
        fontWeight: 800,
        padding: '3px 8px',
        borderRadius: 5,
        letterSpacing: '0.03em',
      }}
    >
      {method}
    </span>
  );
}

function Endpoint({
  method,
  path,
  title,
  description,
  params,
  request,
  response,
}: {
  method: string;
  path: string;
  title: string;
  description: string;
  params?: { name: string; type: string; required: boolean; desc: string }[];
  request?: string;
  response?: string;
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e8edf4',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <MethodBadge method={method} />
        <code style={{ fontSize: 12.5, fontWeight: 700, color: '#111' }}>{path}</code>
      </div>
      <p style={{ fontSize: 13.5, fontWeight: 800, color: '#111', marginBottom: 4 }}>{title}</p>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.6 }}>{description}</p>

      {params && params.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Parameters
          </p>
          <div style={{ border: '1px solid #f0f4f8', borderRadius: 8, overflow: 'hidden' }}>
            {params.map((p, i) => (
              <div
                key={p.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 70px 60px 1fr',
                  gap: 8,
                  padding: '8px 12px',
                  fontSize: 11.5,
                  borderBottom: i < params.length - 1 ? '1px solid #f0f4f8' : 'none',
                  background: i % 2 ? '#fafbfc' : '#fff',
                }}
              >
                <code style={{ fontWeight: 700, color: '#111' }}>{p.name}</code>
                <span style={{ color: '#9ca3af' }}>{p.type}</span>
                <span style={{ color: p.required ? '#dc2626' : '#9ca3af', fontWeight: 600 }}>
                  {p.required ? 'required' : 'optional'}
                </span>
                <span style={{ color: '#6b7280' }}>{p.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {request && (
        <div style={{ marginBottom: 10 }}>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Example request (cURL)
          </p>
          <CodeBlock>{request}</CodeBlock>
        </div>
      )}

      {response && (
        <div>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Example response
          </p>
          <CodeBlock>{response}</CodeBlock>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2 id={id} style={{ fontSize: 17, fontWeight: 900, color: '#111', marginTop: 32, marginBottom: 12, scrollMarginTop: 90 }}>{children}</h2>
  );
}

export default function ApiReferenceContent() {
  return (
    <div style={{ maxWidth: 760 }}>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 4 }}>
        Base URL
      </p>
      <CodeBlock>https://code.mannaplag.mannaanalytics.com/api</CodeBlock>

      <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, marginTop: 16 }}>
        The Manna Plag API gives journal publishers, editorial management systems, and institutional
        repositories programmatic access to plagiarism detection and AI content analysis, so originality
        checking can be built directly into your own workflow.
      </p>

      <SectionHeading id="authentication">Authentication</SectionHeading>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 10 }}>
        Every request requires an API key in the <code>Authorization</code> header. Generate keys from your{' '}
        <a href="https://code.mannaplag.mannaanalytics.com/dashboard/api-keys" style={{ color: '#1a3a8f', fontWeight: 700 }}>
          dashboard
        </a>{' '}
        under <code>API Keys → Generate new key</code>. Keys are prefixed <code>MAPL_</code> and shown only once at creation.
      </p>
      <CodeBlock>{`Authorization: Api-Key MAPL_your_key_here`}</CodeBlock>

      <SectionHeading id="rate-limits">Rate limits &amp; quotas</SectionHeading>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 10 }}>
        Usage is governed by your monthly paper quota. Each document submission counts as one paper.
        Once the limit is reached the API returns <code>HTTP 429</code>. Quotas reset on the 1st of each month.
      </p>
      <div style={{ border: '1px solid #e8edf4', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#f8fafc', padding: '8px 14px' }}>
          {['Plan', 'Papers / month', 'Price'].map(h => (
            <span key={h} style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
          ))}
        </div>
        {[
          ['API Starter', '100', '₹3,000/mo'],
          ['API Growth', '400', '₹10,000/mo'],
          ['API Scale', '2,000', '₹35,000/mo'],
          ['API Enterprise', 'Custom', 'Contact sales'],
        ].map((row, i) => (
          <div key={row[0]} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '9px 14px', borderTop: '1px solid #f0f4f8', background: i % 2 ? '#fafbfc' : '#fff' }}>
            {row.map((cell, j) => (
              <span key={j} style={{ fontSize: 12, fontWeight: j === 0 ? 700 : 500, color: '#1f2937' }}>{cell}</span>
            ))}
          </div>
        ))}
      </div>

      <SectionHeading id="quickstart">Quickstart</SectionHeading>
      <ol style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 2, paddingLeft: 18, marginBottom: 8 }}>
        <li><strong>Generate an API key</strong> from Dashboard → API Keys → Generate new key. Copy it immediately. It is shown only once.</li>
        <li><strong>Verify your key</strong> with <code>GET /auth/me/</code>.</li>
        <li><strong>Upload a document</strong> with <code>POST /submissions/upload/</code>.</li>
        <li><strong>Poll for status</strong> every 15–30 seconds with <code>GET /submissions/{'{submission_id}'}/</code>.</li>
        <li><strong>Retrieve the report</strong> with <code>GET /submissions/reports/{'{report_id}'}/</code>.</li>
      </ol>

      <SectionHeading id="endpoints">Endpoints</SectionHeading>

      <Endpoint
        method="GET"
        path="/auth/me/"
        title="Get account info"
        description="Verify your API key and retrieve your account details. Use this to test authentication."
        request={`curl https://code.mannaplag.mannaanalytics.com/api/auth/me/ \\\n  -H "Authorization: Api-Key MAPL_your_key_here"`}
        response={`{\n  "id": "5e694c3c-ac60-462b-92db-4e602f021b54",\n  "email": "publisher@journal.com",\n  "full_name": "Dr. Priya Sharma",\n  "role": "publisher",\n  "organisation": "Indian Journal of Research",\n  "is_verified": true\n}`}
      />

      <Endpoint
        method="POST"
        path="/submissions/upload/"
        title="Submit a document for plagiarism check"
        description="Upload a PDF or DOCX manuscript. Returns a submission_id to track processing status. Maximum file size 50MB."
        params={[
          { name: 'file', type: 'File', required: true, desc: 'PDF or DOCX file, max 50MB' },
          { name: 'title', type: 'string', required: false, desc: 'Optional document title' },
        ]}
        request={`curl -X POST https://code.mannaplag.mannaanalytics.com/api/submissions/upload/ \\\n  -H "Authorization: Api-Key MAPL_your_key_here" \\\n  -F "file=@research_paper.pdf" \\\n  -F "title=My Research Paper"`}
        response={`{\n  "message": "Manuscript uploaded. Checking in progress.",\n  "submission_id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",\n  "status": "pending",\n  "file_name": "research_paper.pdf"\n}`}
      />

      <Endpoint
        method="GET"
        path="/submissions/{submission_id}/"
        title="Poll submission status"
        description="Check the processing status of a submitted document. Poll every 15–30 seconds. Status values: pending, processing, complete, failed."
        params={[{ name: 'submission_id', type: 'UUID', required: true, desc: 'ID returned from the upload endpoint' }]}
        request={`curl https://code.mannaplag.mannaanalytics.com/api/submissions/{submission_id}/ \\\n  -H "Authorization: Api-Key MAPL_your_key_here"`}
        response={`// While processing\n{\n  "id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",\n  "status": "processing",\n  "file_name": "research_paper.pdf"\n}\n\n// When complete\n{\n  "id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",\n  "status": "complete",\n  "report_id": "7cb9b6a2-1234-4321-abcd-ef0123456789",\n  "overall_similarity": 14.5,\n  "verbatim_score": 8.2,\n  "mosaic_score": 6.3,\n  "flagged_chunks": 12,\n  "total_chunks": 84\n}`}
      />

      <Endpoint
        method="GET"
        path="/submissions/reports/{report_id}/"
        title="Get full plagiarism report"
        description="Retrieve the complete similarity report including matched sources, DOI links, and flagged text."
        params={[{ name: 'report_id', type: 'UUID', required: true, desc: 'report_id from a completed submission' }]}
        request={`curl https://code.mannaplag.mannaanalytics.com/api/submissions/reports/{report_id}/ \\\n  -H "Authorization: Api-Key MAPL_your_key_here"`}
        response={`{\n  "id": "7cb9b6a2-1234-4321-abcd-ef0123456789",\n  "overall_similarity": 14.5,\n  "verbatim_score": 8.2,\n  "mosaic_score": 6.3,\n  "flagged_chunks": 12,\n  "total_chunks": 84,\n  "matches": [\n    {\n      "source_title": "Deep Learning in Medical Imaging",\n      "source_doi": "10.1234/dlmi.2023.001",\n      "source_url": "https://doi.org/10.1234/dlmi.2023.001",\n      "similarity_pct": 8.5,\n      "matched_text": "The convolutional neural network..."\n    }\n  ]\n}`}
      />

      <Endpoint
        method="POST"
        path="/checker/analyze/"
        title="AI content detection"
        description="Detect AI-generated text in a manuscript. Returns an overall AI score and section-level breakdown with flagged sentences. Citations are excluded from scoring."
        params={[{ name: 'file', type: 'File', required: true, desc: 'PDF or DOCX file, max 50MB' }]}
        request={`curl -X POST https://code.mannaplag.mannaanalytics.com/api/checker/analyze/ \\\n  -H "Authorization: Api-Key MAPL_your_key_here" \\\n  -F "file=@research_paper.pdf"`}
        response={`{\n  "id": "9ab12cd3-ef45-6789-abcd-ef0123456789",\n  "status": "complete",\n  "overall_ai_score": 23.5,\n  "file_name": "research_paper.pdf",\n  "word_count": 8420,\n  "sections": [\n    {\n      "section_name": "Introduction",\n      "ai_score": 45.2,\n      "flagged_sentences": [\n        "Large language models have demonstrated..."\n      ]\n    },\n    {\n      "section_name": "Methodology",\n      "ai_score": 4.1,\n      "flagged_sentences": []\n    }\n  ]\n}`}
      />

      <SectionHeading>Support ticket endpoints</SectionHeading>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 10 }}>
        These endpoints power the Support section of your dashboard and use your normal session token
        (not an API key).
      </p>

      <Endpoint
        method="POST"
        path="/tickets/create/"
        title="Create a support ticket"
        description="Open a new support ticket. Returns the generated ticket number."
        params={[
          { name: 'name', type: 'string', required: true, desc: 'Your name' },
          { name: 'email', type: 'string', required: true, desc: 'Contact email' },
          { name: 'subject', type: 'string', required: true, desc: 'Short summary of the issue' },
          { name: 'message', type: 'string', required: true, desc: 'Full description of the issue' },
          { name: 'category', type: 'string', required: false, desc: 'e.g. Billing, Technical, Account' },
          { name: 'priority', type: 'string', required: false, desc: 'Low, Medium, or High' },
          { name: 'source', type: 'string', required: false, desc: 'Where the ticket originated, e.g. Website' },
        ]}
        request={`curl -X POST https://code.mannaplag.mannaanalytics.com/api/tickets/create/ \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Priyanga","email":"priyanga@example.com","subject":"Payment not reflecting","message":"Paid via Razorpay but subscription still shows inactive.","category":"Billing","priority":"High","source":"Website"}'`}
        response={`{\n  "message": "Ticket created successfully.",\n  "ticket_number": "TKT-000001",\n  "ticket_id": 1\n}`}
      />

      <Endpoint
        method="GET"
        path="/tickets/my/"
        title="List my tickets"
        description="Return every support ticket opened by the authenticated user, most recent first."
        request={`curl https://code.mannaplag.mannaanalytics.com/api/tickets/my/ \\\n  -H "Authorization: Bearer <access_token>"`}
        response={`[\n  {\n    "ticket_id": 1,\n    "ticket_number": "TKT-000001",\n    "subject": "Payment not reflecting",\n    "category": "Billing",\n    "priority": "High",\n    "status": "Open",\n    "created_at": "2026-07-10T10:32:10+05:30",\n    "updated_at": "2026-07-10T10:32:10+05:30"\n  }\n]`}
      />

      <Endpoint
        method="GET"
        path="/tickets/{ticket_id}/"
        title="Ticket detail"
        description="Retrieve the full detail of a single ticket, including its message and current status."
        params={[{ name: 'ticket_id', type: 'integer', required: true, desc: 'ID of the ticket to retrieve' }]}
        request={`curl https://code.mannaplag.mannaanalytics.com/api/tickets/1/ \\\n  -H "Authorization: Bearer <access_token>"`}
        response={`{\n  "ticket_id": 1,\n  "ticket_number": "TKT-000001",\n  "name": "Priyanga",\n  "email": "priyanga@example.com",\n  "subject": "Payment not reflecting",\n  "message": "Paid via Razorpay but subscription still shows inactive.",\n  "category": "Billing",\n  "priority": "High",\n  "status": "Open",\n  "created_at": "2026-07-10T10:32:10+05:30",\n  "updated_at": "2026-07-10T10:32:10+05:30"\n}`}
      />

      <Endpoint
        method="POST"
        path="/tickets/{ticket_id}/reopen/"
        title="Reopen a closed ticket"
        description="Move a resolved or closed ticket back to Open status, for example if the issue recurs."
        params={[{ name: 'ticket_id', type: 'integer', required: true, desc: 'ID of the ticket to reopen' }]}
        request={`curl -X POST https://code.mannaplag.mannaanalytics.com/api/tickets/1/reopen/ \\\n  -H "Authorization: Bearer <access_token>"`}
        response={`{\n  "message": "Ticket reopened successfully.",\n  "ticket_id": 1,\n  "status": "Open"\n}`}
      />

      <SectionHeading>Error codes</SectionHeading>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 10 }}>
        All error responses return JSON with an <code>error</code> field describing the problem.
      </p>
      <div style={{ border: '1px solid #e8edf4', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '70px 130px 1fr', background: '#f8fafc', padding: '8px 14px' }}>
          {['Code', 'Status', 'Description'].map(h => (
            <span key={h} style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
          ))}
        </div>
        {[
          ['200', 'OK', 'Request succeeded'],
          ['202', 'Accepted', 'Document uploaded, processing started'],
          ['400', 'Bad Request', 'Invalid file type, missing fields, or file too large (>50MB)'],
          ['401', 'Unauthorized', 'Missing or invalid API key'],
          ['404', 'Not Found', 'Submission or report not found'],
          ['429', 'Too Many Requests', 'Monthly paper quota exceeded — upgrade plan to continue'],
          ['500', 'Server Error', 'Internal error — contact support@mannaanalytics.com'],
        ].map((row, i) => (
          <div key={row[0]} style={{ display: 'grid', gridTemplateColumns: '70px 130px 1fr', padding: '9px 14px', borderTop: '1px solid #f0f4f8', background: i % 2 ? '#fafbfc' : '#fff' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#1f2937' }}>{row[0]}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{row[1]}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{row[2]}</span>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.7, marginBottom: 8 }}>
        Example <code>429</code> response:
      </p>
      <CodeBlock>{`{\n  "error": "Monthly paper limit reached. You have used 400 of 400 papers this month.",\n  "quota_used": 400,\n  "quota_limit": 400,\n  "plan": "API Growth",\n  "upgrade_url": "/choose-plan"\n}`}</CodeBlock>

      <SectionHeading id="support">Support</SectionHeading>
      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.9, marginBottom: 6 }}>
        Email <a href="mailto:support@mannaanalytics.com" style={{ color: '#1a3a8f', fontWeight: 700 }}>support@mannaanalytics.com</a> for
        API support, integration questions, or to report issues.
      </p>
      <ul style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.9, paddingLeft: 18, marginBottom: 10 }}>
        <li>
          Dashboard:{' '}
          <a href="https://code.mannaplag.mannaanalytics.com/dashboard" style={{ color: '#1a3a8f', fontWeight: 700 }}>
            code.mannaplag.mannaanalytics.com/dashboard
          </a>
        </li>
        <li>
          API Keys:{' '}
          <a href="https://code.mannaplag.mannaanalytics.com/dashboard/api-keys" style={{ color: '#1a3a8f', fontWeight: 700 }}>
            code.mannaplag.mannaanalytics.com/dashboard/api-keys
          </a>
        </li>
        <li>
          Plans &amp; Pricing:{' '}
          <a href="https://code.mannaplag.mannaanalytics.com/pricing" style={{ color: '#1a3a8f', fontWeight: 700 }}>
            code.mannaplag.mannaanalytics.com/pricing
          </a>
        </li>
      </ul>
      <p style={{ fontSize: 11.5, color: '#9ca3af', lineHeight: 1.7 }}>
        Manna Analytics Private Limited · 3/343, Town Railway Nagar, Nagercoil 629001, Tamil Nadu, India
      </p>
    </div>
  );
}